import { BehaviorSubject } from 'rxjs';
import OneSignal from 'onesignal-cordova-plugin';

import { inject, Injectable, NgZone } from '@angular/core';
import { BrowserService } from '@core/services/browser/browser.service';
import { ImpersonationService, StorageService, UserService } from '@core/services';
import {
  AlertController,
  IonicSafeString,
  ModalController,
  NavController,
  Platform,
  ToastController,
} from '@ionic/angular/standalone';
import * as Sentry from '@sentry/angular';
import { User } from '@shared/models';
import { DriverStore } from '@shared/stores/driver.store';
import { QueryClient } from '@tanstack/angular-query-experimental';

import { RegisterSamlModalComponent } from '../login/components/register-saml-modal/register-saml-modal.component';
import { ApiService } from './api.service';
import { LoadingService } from './loading.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private storage = inject(StorageService);
  private api = inject(ApiService);
  private toastCtrl = inject(ToastController);
  private navCtrl = inject(NavController);
  private alertCtrl = inject(AlertController);
  private loadingService = inject(LoadingService);
  private userService = inject(UserService);
  private modalCtrl = inject(ModalController);
  #authStateSubject = new BehaviorSubject(false);
  #browser = inject(BrowserService);
  #ngZone: NgZone = inject(NgZone);
  #queryClient = inject(QueryClient);
  #driverStore = inject(DriverStore);
  #platform = inject(Platform);
  #impersonation = inject(ImpersonationService);
  #loggingOut = false;

  async getUser() {
    return (await this.storage.get<User>('me')) || null;
  }

  needsBirthdayCapture(user: User | null): boolean {
    if (!user) return false;
    const flag = !!user?.campus_actual?.verificar_edad_preparatoria;
    const isPrepa = (user?.user_type?.name || '').trim().toLowerCase() === 'preparatoria';
    if (!flag || !isPrepa) return false;
    if (!user?.birthday) return true;
    return !!user?.requires_age_policy_acceptance;
  }

  getUserFromServer() {
    return (
      this.api
        .getData('user')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((res: any) => {
          if (res && res.success && res.user) {
            this.userService.updateUser(res.user);
            this.#checkTermsAndConditions(res.user);
            return this.storage.set('me', res.user).then(() => {
              return res.user;
            });
          }
          return null;
        })
    );
  }

  checkRedirect() {
    this.storage.get('me').then((user) => {
      if (user) {
        this.redirectTo(user);
      } else {
        this.logout();
      }
    });
  }

  getRedirectPath(user: User): string {
    if (!user || !user.active) return '/email-confirmation';
    if (this.needsBirthdayCapture(user)) return '/capture-birthday';
    if (user.type2 === 'transportec-driver' && user.campus_registered > 2) return '/tecservice-driver/stops';
    return '/tecservice-transportec/wallet';
  }

  redirectTo(user) {
    if (!user) {
      this.logout();
      return;
    }
    this.navCtrl.navigateRoot([this.getRedirectPath(user)]);
  }

  async checkRedirectFromServer() {
    try {
      const storedUser = await this.getUser();
      if (storedUser) {
        const serverUser = await this.getUserFromServer();
        if (serverUser) {
          Sentry.setUser({ id: String(serverUser.id), email: serverUser.email });
          this.redirectTo(serverUser);
        }
      }
    } catch (error) {
      console.error('checkRedirectFromServer error:', error);
    }
  }

  async presentToast(msg = '') {
    const toast = await this.toastCtrl.create({
      message: msg,
      duration: 3000,
      position: 'top',
    });
    return await toast.present();
  }

  switchCampus(campusId) {
    this.loadingService.present();
    return this.api
      .putData('profile/campus_current/' + campusId, {})
      .then(async (res) => {
        if (res && res.success && res.user) {
          await this.storage.set('me', res.user);
          await this.#queryClient.cancelQueries();
          await this.#queryClient.invalidateQueries();
          return res.user;
        }
        return null;
      })
      .finally(() => {
        this.loadingService.dismiss();
      });
  }

  async login(form) {
    this.loadingService.present();
    return this.api
      .postData('login', form)
      .then((res) => {
        if (res && res.success) {
          return this.userService.updateUser(res.user).then(() => {
            return this.storage.set('api_token', res.api_token).then(() => {
              if (res.realtrip) {
                this.storage.set('realtrip', res.realtrip);
              }
              Sentry.setUser({ id: String(res.user.id), email: res.user.email });
              this.redirectTo(res.user);
              this.#authStateSubject.next(true);
              this.#queryClient.invalidateQueries();
            });
          });
        } else {
          return res.errors;
        }
      })
      .finally(() => {
        this.loadingService.dismiss();
      });
  }

  loginDriver(form) {
    this.loadingService.present();
    return this.api
      .postData('loginpin', form)
      .then((res) => {
        if (res && res.success) {
          if (res.bus) {
            this.storage.set('bus', res.bus);
          }
          if (res.routes) {
            this.storage.set('routes', res.routes);
          }
          return this.userService.updateUser(res.user).then(() => {
            return this.storage.set('api_token', res.api_token).then(() => {
              if (res.realtrip) {
                this.storage.set('realtrip', res.realtrip);
              }
              Sentry.setUser({ id: String(res.user.id), email: res.user.email });
              this.redirectTo(res.user);
              this.#authStateSubject.next(true);
              this.#queryClient.invalidateQueries();
            });
          });
        } else {
          return res.message;
        }
      })
      .finally(() => {
        this.loadingService.dismiss();
      });
  }

  /**
   * Start impersonating a user. Saves the admin's session, swaps in a short-lived
   * token for the target, resets cached state, and re-renders the app as that user.
   */
  async impersonate(userId: number): Promise<boolean> {
    this.loadingService.present();
    try {
      const adminMe = await this.storage.get<User>('me');
      const adminToken = await this.storage.get<string>('api_token');
      const res = await this.api.postData('impersonate', { user_id: userId });
      if (res && res.success && res.user && res.api_token) {
        await this.#impersonation.begin(adminMe, adminToken, res.user);
        await this.userService.updateUser(res.user);
        await this.storage.set('api_token', res.api_token);
        Sentry.setUser({ id: String(res.user.id), email: res.user.email });
        await this.#queryClient.cancelQueries();
        this.#queryClient.resetQueries();
        this.#driverStore.reset();
        this.#authStateSubject.next(true);
        this.redirectTo(res.user);
        return true;
      }
      this.presentToast(res?.message || 'No se pudo suplantar a este usuario.');
      return false;
    } finally {
      this.loadingService.dismiss();
    }
  }

  /**
   * Exit impersonation: revoke the impersonation token server-side, restore the
   * admin's saved session, and re-render the app as the admin.
   */
  async exitImpersonation(): Promise<void> {
    this.loadingService.present();
    try {
      // Revoke the impersonation token while it is still the active token.
      try {
        await this.api.postData('impersonate/stop', {});
      } catch {
        /* best effort — proceed with local restore regardless */
      }

      const { adminMe, adminToken } = await this.#impersonation.end();
      if (!adminMe || !adminToken) {
        // Nothing to restore — fall back to a clean logout.
        await this.logout();
        return;
      }

      await this.userService.updateUser(adminMe);
      await this.storage.set('api_token', adminToken);
      Sentry.setUser({ id: String(adminMe.id), email: adminMe.email });
      await this.#queryClient.cancelQueries();
      this.#queryClient.resetQueries();
      this.#driverStore.reset();
      this.#authStateSubject.next(true);
      this.redirectTo(adminMe);
    } finally {
      this.loadingService.dismiss();
    }
  }

  register(form) {
    this.loadingService.present();
    return this.api
      .postData('' + form.endpoint, form)
      .then((res) => {
        if (res && res.success) {
          return this.userService.updateUser(res.user).then(() => {
            return this.storage.set('api_token', res.api_token).then(() => {
              this.redirectTo(res.user);
              this.#authStateSubject.next(true);
              this.#queryClient.invalidateQueries();
            });
          });
        } else {
          return res.errors;
        }
      })
      .finally(() => {
        this.loadingService.dismiss();
      });
  }

  update(form) {
    this.loadingService.present();
    return this.api
      .putData('user', form)
      .then((res) => {
        if (res && res.success) {
          return this.storage.set('me', res.user).then(() => {
            this.#authStateSubject.next(true);
            return res.user;
          });
        } else {
          return res.errors;
        }
      })
      .finally(() => {
        this.loadingService.dismiss();
      });
  }

  async logout() {
    if (this.#loggingOut) return;
    this.#loggingOut = true;

    try {
      Sentry.setUser(null);

      // Unlink device from user so push notifications stop arriving
      if (this.#platform.is('hybrid')) {
        OneSignal.logout();
      }

      // Immediately null out user$ observable to prevent null-access in components
      this.userService.removeFromLocalStorage();

      await this.#queryClient.cancelQueries();
      this.#queryClient.resetQueries();
      this.#driverStore.reset();
      await this.#impersonation.clear();

      // Clear all stored credentials atomically
      await Promise.all([
        this.storage.remove('me'),
        this.storage.remove('api_token'),
        this.storage.remove('subscription'),
        this.storage.remove('realtrip'),
        this.storage.remove('bus'),
        this.storage.remove('routes'),
        // Keys owned by RecentStopsService — cleared here to avoid cross-user leaks on shared devices.
        this.storage.remove('ticket-search:recents:go'),
        this.storage.remove('ticket-search:recents:return'),
      ]);

      await Promise.allSettled([
        this.loadingService.dismiss(),
        this.modalCtrl.getTop().then((m) => m?.dismiss()),
        this.alertCtrl.getTop().then((a) => a?.dismiss()),
        this.toastCtrl.getTop().then((t) => t?.dismiss()),
      ]);

      this.#authStateSubject.next(false);
      this.navCtrl.navigateRoot(['/']);
    } finally {
      this.#loggingOut = false;
    }
  }

  resendMail() {
    this.loadingService.present('Reenviando correo...');
    this.api
      .postData('resend/email', {})
      .then(
        (res) => {
          this.presentToast('Correo enviado exitosamente.');
        },
        (err) => {
          this.presentToast('Error de conexión con el servidor. Intente nuevamente más tarde.');
        }
      )
      .finally(() => {
        this.loadingService.dismiss();
      });
  }

  forgotMail(form) {
    this.loadingService.present('Enviando correo...');
    this.api
      .postData('forgot', form)
      .then(
        (res) => {
          this.presentToast('Correo enviado exitosamente.');
        },
        (err) => {
          this.presentToast('Error de conexión con el servidor. Intente nuevamente más tarde.');
        }
      )
      .finally(() => {
        this.loadingService.dismiss();
      });
  }

  async isAuthenticated() {
    const user = (await this.storage.get('me')) || null;
    if (user) {
      return true;
    } else {
      return false;
    }
  }

  async hasMealplan() {
    const user = (await this.storage.get<User>('me')) || null;
    if (user && user.borrego === 1) {
      return true;
    } else {
      return false;
    }
  }

  async canExpresotec() {
    const user = (await this.storage.get<User>('me')) || null;
    if (
      user &&
      typeof user.campus_registered !== 'undefined' &&
      user.campus_registered === 1 &&
      user.campus_current === 1 &&
      (user.type === 'Estudiante' || user.type === 'Representativo' || user.type === 'Colaborador')
    ) {
      return true;
    } else {
      return false;
    }
  }

  async getMembership() {
    return this.api
      .getData('membership')
      .then((res) => {
        if (res && res.subscription) {
          return true;
        } else {
          return false;
        }
      })
      .catch(() => {
        return false;
      });
  }

  async mayExpresotec() {
    const user = (await this.storage.get<User>('me')) || null;
    if (
      user &&
      typeof user.campus_registered !== 'undefined' &&
      user.campus_registered === 1 &&
      !user.expresotec &&
      user.campus_current === 1 &&
      (user.type === 'Estudiante' || user.type === 'Representativo' || user.type === 'Colaborador')
    ) {
      return this.api
        .getData('membership')
        .then((res) => {
          if (res && res.school_period) {
            return true;
          } else {
            return false;
          }
        })
        .catch(() => {
          return false;
        });
    }
    return false;
  }

  async canTransportec() {
    const user = (await this.storage.get<User>('me')) || null;
    if (
      user &&
      typeof user.campus_registered !== 'undefined' &&
      user.campus_registered > 2 &&
      user.campus_current > 2 &&
      (user.type === 'Estudiante' || user.type === 'Representativo' || user.type === 'Colaborador')
    ) {
      return true;
    } else {
      return false;
    }
  }

  async getBusiness() {
    const user = (await this.storage.get<User>('me')) || null;
    if (user.type2 === 'business') {
      let business = await this.storage.get('business');
      if (!business) {
        business = user.business[0];
      }
      if (typeof business === 'string') {
        try {
          business = JSON.parse(business);
        } catch (e) {
          console.error(e);
        }
      }
      await this.storage.set('business', business);
      return business;
    } else {
      this.logout();
      return false;
    }
  }

  async askExit() {
    const alert = await this.alertCtrl.create({
      header: 'Cerrar TecService',
      message: '¿Esta seguro que quiere salir de la aplicación?',
      buttons: [
        {
          text: 'Cancelar',
          role: 'cancel',
          handler: () => {
            console.log('Application exit prevented!');
          },
        },
        {
          text: 'Cerrar App',
          handler: () => {
            navigator['app'].exitApp();
          },
        },
      ],
    });
    alert.present();
  }

  getBalance() {
    return this.api
      .getData('balance')
      .then((res) => {
        if (res) {
          return res;
        }
      })
      .catch((err) => {
        this.presentToast('Error de conexión con el servidor. Intente nuevamente más tarde.');
      })
      .finally(() => {});
  }

  async getSubscription_() {
    const subscription = (await this.storage.get('subscription')) || null;
    if (subscription) {
      return subscription;
    } else {
      return this.api
        .getData('suscription')
        .then((res) => {
          if (res) {
            this.storage.set('subscription', res.data);
            return res.data;
          }
        })
        .catch((err) => {
          this.presentToast('Error de conexión con el servidor. Intente nuevamente más tarde.');
        })
        .finally(() => {});
    }
  }

  async getSubscription() {
    return this.api
      .getData('suscription')
      .then((res) => {
        if (!res || !res.success) {
          return;
        }
        this.storage.set('subscription', res.data);
        return res.data;
      })
      .catch((err) => {
        this.presentToast('Error de conexión con el servidor. Intente nuevamente más tarde.');
      })
      .finally(() => {});
  }

  get(type) {
    return this.api
      .getData('' + type)
      .then((res) => {
        if (res) {
          return res;
        }
      })
      .catch((err) => {
        this.presentToast('Error de conexión con el servidor. Intente nuevamente más tarde.');
      })
      .finally(() => {});
  }

  async singleLogOut() {
    try {
      const isSsoLogin = await this.storage.get('ssologin');
      if (!isSsoLogin) {
        this.logout();
        return;
      }
      this.loadingService.present();

      const sloRedirect = await this.api.postData('logout', {});
      this.loadingService.dismiss();
      const ssoUrl = sloRedirect.redirect_to;
      if (!ssoUrl) {
        this.logout();
        return;
      }
      const listener = await this.#browser.addUrlChangeListener(async (event) => {
        if (event.url.includes('saml2/success')) {
          this.#ngZone.run(() => {
            this.logout();
          });
          listener.remove();
          await this.#browser.close();
        }
      });
      await this.#browser.openInApp(ssoUrl, { title: 'Cerrando sesión...', showArrow: false });
    } catch (error) {
      this.loadingService.dismiss();
    }
  }

  async singleSignOn() {
    try {
      this.loadingService.present();

      const ssoRedirect = await this.api.postData('saml2/tec', {});
      this.loadingService.dismiss();
      const ssoUrl = ssoRedirect.redirect_to;
      const listener = await this.#browser.addUrlChangeListener(async (event) => {
        if (!event.url.includes('saml2/success')) {
          return;
        }
        const queryParams = this.getUrlParametersFromUrl(event.url);
        if (queryParams['action'] === 'register') {
          this.openRegisterSamlModal(queryParams);
          listener.remove();
          await this.#browser.close();
          return;
        }
        if (queryParams['token'] && queryParams['id']) {
          await this.storage.set('api_token', queryParams['token']);
          const user = {
            id: +queryParams['id'],
            name: queryParams['name'],
            lastname: queryParams['lastname'],
            fullname: queryParams['fullname'],
            roster: queryParams['roster'],
            email: queryParams['email'],
            role: queryParams['role'],
            enrollment: queryParams['enrollment'],
            active: +queryParams['active'],
            campus_registered: +queryParams['campus_registered'],
            campus_current: +queryParams['campus_current'],
            type: queryParams['type'],
            type2: queryParams['type2'],
            status: queryParams['status'],
            campus_registered_name: queryParams['campus_registered_name'],
            user_type_id: +queryParams['user_type_id'],
          };
          await this.userService.updateUser(user);
          await this.storage.set('ssologin', true);
          Sentry.setUser({ id: String(user.id), email: user.email });
          this.#authStateSubject.next(true);
          this.#queryClient.invalidateQueries();
          listener.remove();
          await this.#browser.close();
          this.#ngZone.run(() => {
            this.navCtrl.navigateRoot(['/tecservice-transportec/wallet']);
          });
        }
      });
      await this.#browser.openInApp(ssoUrl);
    } catch (error) {
      this.loadingService.dismiss();
    }
  }

  private async presentTermsAndConditions(queryParams) {
    const alert = await this.alertCtrl.create({
      header: 'Términos y condiciones',
      message: new IonicSafeString(
        `Para poder continuar, es necesario que aceptes los <a href="https://tecservice.app/terminosycondiciones?campus=${queryParams['campus_registered']}" target="_blank">términos y condiciones</a>.`
      ),
      buttons: [
        {
          text: 'Cancelar',
          role: 'cancel',
          handler: () => {
            this.logout();
          },
        },
        {
          text: 'Aceptar',
          handler: () => {
            this.openRegisterSamlModal(queryParams);
          },
        },
      ],
    });
    alert.present();
  }

  private openTerms(campusId: number) {
    this.#browser.openInApp('https://tecservice.app/terminosycondiciones?campus=' + campusId);
  }

  private async openRegisterSamlModal(user: Partial<User>, mode = 'register') {
    const openedModal = await this.modalCtrl.getTop();
    if (openedModal) {
      return;
    }

    const modal = await this.modalCtrl.create({
      component: RegisterSamlModalComponent,
      componentProps: { user, mode },
    });

    modal.onDidDismiss().then((response) => {
      if (response?.data && response?.data?.api_token && response?.data?.user) {
        this.storage.set('api_token', response.data.api_token);
        this.userService.updateUser(response.data.user);
        this.#authStateSubject.next(true);
        this.#queryClient.invalidateQueries();
        this.#ngZone.run(() => {
          this.navCtrl.navigateRoot(['/tecservice-transportec/wallet']);
        });
      }
    });

    return await modal.present();
  }

  private getUrlParametersFromUrl(url: string): Record<string, string> {
    const queryString = url.split('?')[1];
    const urlParams = new URLSearchParams(queryString);
    const params: Record<string, string> = {};
    urlParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }

  #checkTermsAndConditions(user: User) {
    if (user && !user.terms) {
      this.openRegisterSamlModal(user, 'update');
    }
  }
}
